/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */


import React, { useMemo } from "react";
import { sanitizeHtml } from "../../../utils/sanitize";
import styled from "styled-components";
import Button from "../../ui/Button";
import Text from "../../ui/Text";
import Flex from "../../ui/Flex";
import Stack from "../../ui/Stack";
import TextCircleDivider from "../../ui/TextCircleDivider";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import { Modal, ModalContent, ModalBody, ModalHeader, ModalCloseButton, ModalImage, ModalTitle } from "../../ui/Modal";
import { RECURRING_SESSIONS_LABEL_TEXT } from "../../../constants/constants";
import BookingStepItem from "../steps/BookingStepItem";
import T from "../../ui/Translation";
import FormatPrice from "../../formatters/FormatPrice";
import FormatDuration from "../../formatters/FormatDuration";

// Building blocks of the service card, shared with ClassItem so both cards look the same.
export const StyledServiceItem = styled(BookingStepItem)`
  position: relative;
  display: flex;
  gap: 1rem;
  padding: 12px;

  ${({ theme }) => theme.containerMedia.sm} {
    padding: 8px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ServiceItemInner = styled(Stack)`
  width: 100%;
  align-self: stretch;
`;

const ServiceItemMedia = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6.875rem;
  height: 6.875rem;
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;

  ${({ theme }) => theme.containerMedia.sm} {
    width: 5rem;
    height: 5rem;
  }
`;

const ServiceItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ServiceItemInfo = styled(Flex)`
  width: 100%;
`;

export const ServiceItemInfoInner = styled(Stack)`
  width: 100%;
`;

export const ServiceItemInfoHeader = styled(Flex)`
  width: 100%;
`;

export const ServiceItemButton = styled((props) => <Button {...props} />)`
  margin-top: 2px;
  flex-shrink: 0;

  ${({ theme }) => theme.containerMedia.sm} {
    position: absolute;
    top: 8px;
    right: 8px;
  }
`;

export const ServiceFooter = styled(Flex)`
  padding-bottom: 0.375rem;
`;

export const MetaGroup = styled(Stack)`
  align-self: stretch;
  margin-left: auto;
  text-align: right;
`;

const RecurringPriceSuffix = ({ recurringSettings }) => {
  if (!recurringSettings) return null;

  const labelKey = recurringSettings.price_per_session ? "per_session" : "per_pack";

  return (
    <>
      {" / "}
      <T>{labelKey}</T>
    </>
  );
};

export const ServiceMedia = ({ image, name, placeholder = <Icon.ServicePlaceholderIcon size={70} /> }) => (
  <ServiceItemMedia>
    {image ? <ServiceItemImage src={image} alt={name} /> : placeholder}
  </ServiceItemMedia>
);

export const ServicePriceAndDuration = ({ service, isRecurring }) => {
  const { price, duration, currency, recurring_settings } = service;
  const hasPrice = Boolean(price && price > 0);
  const showDuration = !isRecurring && Boolean(duration && duration > 0);
  const showDivider = hasPrice && showDuration;

  return (
    <Flex $align="center" $gap="0.25rem">
      {hasPrice && (
        <Text color="dark" weight="bold" size="large">
          <FormatPrice value={price} currency={currency} />
          <RecurringPriceSuffix recurringSettings={isRecurring ? recurring_settings : null} />
        </Text>
      )}

      {showDivider && <TextCircleDivider />}
      {showDuration && <ServiceDuration service={service} />}
    </Flex>
  );
};

export const ServiceDepositPrice = ({ service, isRecurring }) => {
  const { deposit_price, currency, recurring_settings } = service;

  return (
    <Text color="dark" weight="regular" size="default">
      <FormatPrice value={deposit_price} currency={currency} /> <T>deposit_price</T>
      <RecurringPriceSuffix recurringSettings={isRecurring ? recurring_settings : null} />
    </Text>
  );
};

export const ServiceDuration = ({ service, isRecurring }) => {
  const { duration, recurring_settings } = service;

  const sessionLabel = useMemo(() => {
    if (!isRecurring || !recurring_settings) return null;

    const { repeat_count } = recurring_settings;
    const labelKey = repeat_count <= 1
      ? RECURRING_SESSIONS_LABEL_TEXT.SINGLE
      : RECURRING_SESSIONS_LABEL_TEXT.MULTIPLE;

    return (
      <>
        {" / "}
        {repeat_count} <T>{labelKey}</T>
      </>
    );
  }, [isRecurring, recurring_settings]);

  return (
    <Text>
      <FormatDuration value={duration} type={FormatDuration.TYPE.HOUR} />
      {sessionLabel}
    </Text>
  );
};

// Price/duration and deposit lines; nothing when the service has none of them.
export const ServicePricing = ({ service, isRecurring }) => {
  const hasPriceOrDuration = service.price > 0 || service.duration > 0;
  const hasDepositPrice = service.deposit_price > 0;
  if (!hasPriceOrDuration && !hasDepositPrice) return null;

  return (
    <Stack $gap="0.125rem">
      {hasPriceOrDuration && <ServicePriceAndDuration service={service} isRecurring={isRecurring} />}
      {hasDepositPrice && <ServiceDepositPrice service={service} isRecurring={isRecurring} />}
    </Stack>
  );
};

export const MembershipBadge = ({ applied }) =>
  applied ? (
    <Badge color="success" icon={<Icon.Check size={Icon.SIZE.SM} />} title="Membership applied" />
  ) : (
    <Badge color="neutral" title="Membership required" />
  );

export const ServiceDescriptionModal = ({ isOpen, onClose, service, image }) => (
  <Modal isOpen={isOpen} onClose={onClose}>
    <ModalContent>
      {image ? (
        <>
          <ModalCloseButton />
          <ModalImage src={image} alt={service.name} />
        </>
      ) : (
        <ModalHeader>
          <ModalTitle>{service.name}</ModalTitle>
        </ModalHeader>
      )}
      <ModalBody>
        {image && <ModalTitle>{service.name}</ModalTitle>}
        <Text dangerouslySetInnerHTML={{ __html: sanitizeHtml(service.description) }} />
      </ModalBody>
    </ModalContent>
  </Modal>
);
